import React, { useCallback, useState } from 'react';
import { AlertTriangle, CheckCircle2, History, Sparkles, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Localized } from '../../types/notice';
import type { AttentionTarget } from '../../utils/requestDashboard';
import type { BriefingDraft, TerritorialBriefing } from '../../utils/territorialBriefing';
import { DATA_AS_OF } from '../../data/requestDashboard';
import { BriefingCard } from './BriefingCard';
import { BriefingDraftDialog } from './BriefingDraftDialog';
interface TerritorialBriefingPanelProps {
  briefing: TerritorialBriefing;
  scopeLabel: Localized | null;
  onOpen: (target: AttentionTarget) => void;
  /** Shown in the third column, next to the result to share (e.g. ATNA). */
  aside?: React.ReactNode;
}

/** "Votre briefing territorial": 3 issues + 1 result to share, prepared by ATNA. */
export function TerritorialBriefingPanel({
  briefing,
  scopeLabel,
  onOpen,
  aside
}: TerritorialBriefingPanelProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [draft, setDraft] = useState<{
    itemId: string;
    draft: BriefingDraft;
  } | null>(null);
  const [sent, setSent] = useState<string[]>([]);
  const closeDraft = useCallback(() => setDraft(null), []);
  const {
    since
  } = briefing;
  const send = () => {
    if (!draft) return;
    setSent(s => [...s, `${draft.itemId}:${draft.draft.kind}`]);
    setDraft(null);
  };
  const sinceCols = [{
    key: 'new',
    label: t('Nouveaux enjeux', 'New issues'),
    icon: TrendingUp,
    color: 'text-amber-600',
    data: since.newIssues
  }, {
    key: 'worse',
    label: t('Situations aggravées', 'Worsened'),
    icon: AlertTriangle,
    color: 'text-red-600',
    data: since.worsened
  }, {
    key: 'resolved',
    label: t('Problèmes résolus', 'Resolved'),
    icon: CheckCircle2,
    color: 'text-emerald-600',
    data: since.resolved
  }];
  return <section className="border border-slate-200 rounded-xl shadow-sm bg-white font-body overflow-hidden" data-id="element-5561" data-mp="10wb3brkcan7c">
      <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-start justify-between gap-2" data-id="element-5562" data-mp="15wp1mhw52is6">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5563" data-mp="32tblyj79j3rb">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5564" data-mp="32rqq8q97perv">
            {scopeLabel ? t(`Votre briefing territorial · ${scopeLabel.fr}`, `Your territorial briefing · ${scopeLabel.en}`) : t('Votre briefing territorial', 'Your territorial briefing')}
          </h2>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-[11px] font-semibold" data-id="element-5565" data-mp="3ljb5kqkc3qnc">
            <Sparkles className="w-3 h-3" data-id="element-5566" data-mp="3hg4sp02pv17w" />
            {t('Préparé par ATNA', 'Prepared by ATNA')}
          </span>
        </div>
        <span className="text-xs text-slate-400 shrink-0" data-id="element-5567" data-mp="262sx1ch41owt">{DATA_AS_OF[lang]}</span>
      </div>

      <div className="px-6 pt-5" data-id="element-5568" data-mp="213povj5u32km">
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3" data-id="element-5569" data-mp="213g7gjyuu3qj">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2" data-id="element-5570" data-mp="gm71p2hulc9q">
            <History className="w-3.5 h-3.5" data-id="element-5571" data-mp="lk8krnf9xbl7" />
            {t('Depuis votre dernière visite', 'Since your last visit')}
            <span className="font-normal text-slate-400" data-id="element-5572" data-mp="plamqc0ks1qv">· {since.lastVisit[lang]}</span>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-5573" data-mp="ue3oqs4nsvz9">
            {sinceCols.map(c => {
            const Icon = c.icon;
            const zero = c.data.count === 0;
            return <div key={c.key} className="flex items-start gap-2.5" data-id="element-5574" data-mp="149yhwh6ie9ga">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${zero ? 'text-slate-300' : c.color}`} data-id="element-5575" data-mp="19a2wu2rrqw4e" />
                  <div className="min-w-0" data-id="element-5576" data-mp="1dc7o4uuqvgnv">
                    <dt className="text-xs text-slate-500" data-id="element-5577" data-mp="1db4w2jlor86d">{c.label}</dt>
                    <dd className="text-sm" data-id="element-5578" data-mp="3b30ucbwhqcvo">
                      <span className={`font-bold font-heading ${zero ? 'text-slate-400' : 'text-[#0F172A]'}`} data-id="element-5579" data-mp="3f3b0boka93aj">
                        {c.data.count}
                      </span>{' '}
                      <span className="text-slate-600" data-id="element-5580" data-mp="2jv7eume81ylt">{c.data.detail[lang]}</span>
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </div>
      </div>

      {/* Row 1: three issues. Row 2: the 4th issue, the result to share, then ATNA. */}
      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5581" data-mp="2oqef1k8a8l5z">
        {[...briefing.issues, briefing.result].map(item => <BriefingCard key={item.id} item={item} sentKinds={sent} onPrepare={d => setDraft({
        itemId: item.id,
        draft: d
      })} onOpen={onOpen} data-id="element-5582" data-mp="1w41ym64dd4az" />)}
        {aside}
      </div>

      <BriefingDraftDialog draft={draft?.draft ?? null} onClose={closeDraft} onSend={send} data-id="element-5583" data-mp="25zmvxqyhtgee" />
    </section>;
}